/**
 * ============================================================================
 * KbTreeSidebar — Árvore de páginas do space, agrupadas por Coleção.
 * ----------------------------------------------------------------------------
 * Estrutura:
 *   Coleção (📁)
 *     ├─ Subcoleção
 *     │    └─ Página (com subpáginas via parent_id)
 *     └─ Página direto na coleção
 *   Sem coleção
 *     └─ Páginas órfãs
 *
 * Sem drag-and-drop (essa versão é só navegação/visualização).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.1.0 - Adiciona nível externo de agrupamento por
 *            coleção/subcoleção; auto-expande a coleção da página ativa.
 *   RELEASE: 2026-07-07 v1.0.0 - F1-Fix.
 * ============================================================================
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronDown, FileText } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useKbPagesTree, useKbCollections, type KbCollectionRow } from "@/hooks/kb/useKb";
import { cn } from "@/lib/utils";

interface Props {
  spaceId: string;
  activePageId?: string;
}

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
interface PageNode {
  id: string;
  title: string;
  parent_id: string | null;
  collection_id: string | null;
  status?: string;
  children: PageNode[];
}

// ---------------------------------------------------------------------------
// Constrói a árvore de páginas por parent_id, restrita a um conjunto (rows).
// Páginas cujo parent não esteja no conjunto viram raízes desse conjunto.
// ---------------------------------------------------------------------------
function buildPageTree(rows: any[]): PageNode[] {
  const map = new Map<string, PageNode>();
  rows.forEach((r) =>
    map.set(r.id, {
      id: r.id,
      title: r.title,
      parent_id: r.parent_id ?? null,
      collection_id: r.collection_id ?? null,
      status: r.status,
      children: [],
    }),
  );
  const roots: PageNode[] = [];
  map.forEach((n) => {
    if (n.parent_id && map.has(n.parent_id)) map.get(n.parent_id)!.children.push(n);
    else roots.push(n);
  });
  return roots;
}

// ---------------------------------------------------------------------------
// Nó: Página (recursivo por parent_id)
// ---------------------------------------------------------------------------
function PageTreeNode({
  node, spaceId, activePageId, depth,
}: { node: PageNode; spaceId: string; activePageId?: string; depth: number }) {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children.length > 0;
  const active = node.id === activePageId;

  return (
    <div>
      <div
        className={cn(
          "group flex items-center gap-1 rounded px-0.5 py-1 text-sm hover:bg-accent",
          active && "bg-accent font-medium",
        )}
        style={{ paddingLeft: 2 + depth * 10 }}
      >
        {hasChildren ? (
          <button onClick={() => setOpen((v) => !v)} className="p-0.5 hover:bg-muted rounded">
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <Link to={`/tenant/kb/${spaceId}/pages/${node.id}`} className="truncate flex-1">
          {node.title || "(sem título)"}
        </Link>
      </div>
      {open && hasChildren && (
        <div>
          {node.children.map((c) => (
            <PageTreeNode
              key={c.id}
              node={c}
              spaceId={spaceId}
              activePageId={activePageId}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nó: Coleção (recursivo por parent_id da coleção). Renderiza subcoleções
// e, no fim, as páginas raiz daquela coleção.
// ---------------------------------------------------------------------------
function CollectionNode({
  collection, spaceId, activePageId, depth,
  collectionsByParent, pagesByCollection, expanded, onToggle,
}: {
  collection: KbCollectionRow;
  spaceId: string;
  activePageId?: string;
  depth: number;
  collectionsByParent: Map<string | null, KbCollectionRow[]>;
  pagesByCollection: Map<string | null, PageNode[]>;
  expanded: Set<string>;
  onToggle: (id: string) => void;
}) {
  const isOpen = expanded.has(collection.id);
  const subCols = collectionsByParent.get(collection.id) ?? [];
  const rootPages = pagesByCollection.get(collection.id) ?? [];
  const hasChildren = subCols.length > 0 || rootPages.length > 0;

  return (
    <div>
      <div
        className="group flex items-center gap-1 rounded px-0.5 py-1 text-sm hover:bg-accent"
        style={{ paddingLeft: 2 + depth * 10 }}
      >
        {hasChildren ? (
          <button onClick={() => onToggle(collection.id)} className="p-0.5 hover:bg-muted rounded">
            {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <span className="text-sm leading-none select-none">
          {collection.icon || (isOpen ? "📂" : "📁")}
        </span>
        <span className="flex-1 truncate font-medium">{collection.name}</span>
        {collection.page_count > 0 && (
          <Badge variant="outline" className="h-4 px-1.5 text-[10px] font-normal">
            {collection.page_count}
          </Badge>
        )}
      </div>
      {isOpen && hasChildren && (
        <div>
          {subCols.map((sc) => (
            <CollectionNode
              key={sc.id}
              collection={sc}
              spaceId={spaceId}
              activePageId={activePageId}
              depth={depth + 1}
              collectionsByParent={collectionsByParent}
              pagesByCollection={pagesByCollection}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
          {rootPages.map((p) => (
            <PageTreeNode
              key={p.id}
              node={p}
              spaceId={spaceId}
              activePageId={activePageId}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export function KbTreeSidebar({ spaceId, activePageId }: Props) {
  const pagesQuery = useKbPagesTree(spaceId);
  const collectionsQuery = useKbCollections(spaceId);

  const pages = (pagesQuery.data ?? []) as any[];
  const collections = collectionsQuery.data ?? [];

  // Indexa coleções por parent, ordenadas por col_position
  const collectionsByParent = useMemo(() => {
    const m = new Map<string | null, KbCollectionRow[]>();
    collections.forEach((c) => {
      const key = c.parent_id ?? null;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(c);
    });
    m.forEach((arr) => arr.sort((a, b) => a.col_position - b.col_position));
    return m;
  }, [collections]);

  // Para cada coleção, monta a árvore de páginas raiz daquela coleção
  // (páginas cujo parent também está na mesma coleção viram filhas na árvore).
  const pagesByCollection = useMemo(() => {
    const groups = new Map<string | null, any[]>();
    pages.forEach((p) => {
      const key = p.collection_id ?? null;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    });
    const result = new Map<string | null, PageNode[]>();
    groups.forEach((rows, key) => {
      result.set(key, buildPageTree(rows));
    });
    return result;
  }, [pages]);

  // Estado de expansão de coleções (memória em componente; não persistido aqui
  // para não colidir com o storage do KbSpaceCollectionsTree).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Auto-expande: raízes na primeira renderização; ancestrais da página ativa.
  useEffect(() => {
    if (collections.length === 0) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      // Sempre garante raízes abertas
      collections.filter((c) => !c.parent_id).forEach((c) => next.add(c.id));
      // Se há página ativa, abre a coleção dela e ancestrais
      if (activePageId) {
        const page = pages.find((p) => p.id === activePageId);
        let cid: string | null | undefined = page?.collection_id ?? null;
        const byId = new Map(collections.map((c) => [c.id, c] as const));
        while (cid) {
          next.add(cid);
          cid = byId.get(cid)?.parent_id ?? null;
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections.length, activePageId, pages.length]);

  if (pagesQuery.isLoading || collectionsQuery.isLoading) {
    return (
      <div className="p-2 space-y-2">
        <Skeleton className="h-5" />
        <Skeleton className="h-5 w-4/5" />
      </div>
    );
  }

  const rootCollections = collectionsByParent.get(null) ?? [];
  const orphanPages = pagesByCollection.get(null) ?? [];

  if (rootCollections.length === 0 && orphanPages.length === 0) {
    return <div className="p-3 text-xs text-muted-foreground">Nenhuma página neste space.</div>;
  }

  return (
    <div className="p-2">
      {rootCollections.map((c) => (
        <CollectionNode
          key={c.id}
          collection={c}
          spaceId={spaceId}
          activePageId={activePageId}
          depth={0}
          collectionsByParent={collectionsByParent}
          pagesByCollection={pagesByCollection}
          expanded={expanded}
          onToggle={toggle}
        />
      ))}

      {orphanPages.length > 0 && (
        <div className="mt-2">
          <div className="px-2 py-1 text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">
            Sem coleção
          </div>
          {orphanPages.map((p) => (
            <PageTreeNode
              key={p.id}
              node={p}
              spaceId={spaceId}
              activePageId={activePageId}
              depth={0}
            />
          ))}
        </div>
      )}
    </div>
  );
}
